import { RouteLocationNormalized } from "vue-router";
import Authenticated from "@/layouts/app/Authenticated.vue";
import AppLayout from "@/layouts/game/App.vue";
import { fetchTokenQuery, fetchUserQuery, prefetchUserRoles } from "@/queries/user.query";
import { fetchOrganisationsQuery } from "@/queries/organisation.query";
import { api } from "@/api/client";
import { invitationApi } from "@/api/invitation.api";
import { queryClient } from "@/queries/queryClient";
import ExperienceLayout from "@/layouts/experience/ExperienceLayout.vue";

enum State {
  START = 'start',
  URL_IS_INVITATION_LINK = 'url-is-invitation-link',
  GET_INVITATION_DETAILS = 'get-invitation-details',
  IS_LINK_PERSONAL = 'is-link-personal',
  LINK_ID_MATCH_TOKEN = 'link-id-match-token',
  ALREADY_ON_LOGIN_PAGE = 'already-on-login-page',
  REGISTER_PAGE_URL_AND_GO_TO_LOGIN = 'register-page-url-and-go-to-login',
  IS_LOGGED_IN = 'is-logged-in',
  ACCEPT_INVITATION = 'accept-invitation',
  IS_ON_LOGIN_OR_INVITE_PAGE = 'is-on-login-or-invite-page',
  REDIRECT_TO_HOME_PAGE = 'redirect-to-home-page',
  IS_AUTHORIZED = 'authorized',
  NOT_AUTHORIZED = 'not-authorized',
  STOP = 'stop',
}

async function getOrganisationId(): Promise<string> {
  const currentOrganisationId = localStorage.getItem("current_organisation_id");
  try {
    const organisations = await fetchOrganisationsQuery();
    if (!currentOrganisationId) {
      return organisations?.[0].id;
    } else {
      if (organisations.find((o) => o.id === currentOrganisationId)) {
        return currentOrganisationId;
      } else {
        return organisations?.[0]?.id;
      }
    }
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

function removeTokenFromCache() {
  localStorage.removeItem('auth_token');
  delete api.defaults.headers.common["Authorization"];
  delete axiosInstance.defaults.headers.common["Authorization"];
  queryClient.invalidateQueries({ queryKey: ['app-token'] });
}

function registerCurrentUrl(to: RouteLocationNormalized, accountId: string) {
  const redirectUrl = to.fullPath;
  if (to.name !== "auth" && to.name !== "notFoundPage" && to.name !== "maintenancePage" && to.name !== "noCompany") {
    localStorage.setItem("redirectUrl", redirectUrl);
  }
  if (accountId) {
    localStorage.setItem("accountId", accountId);
  } else {
    localStorage.removeItem("accountId");
  }
}

function isTalentPage() {
  const host = window.location.host;
  return host.includes("game");
}

function isExperiencePage() {
  const host = window.location.host;
  return host.includes("experience");
}

async function redirectToHomePage(to: RouteLocationNormalized) {
    if (isTalentPage()) {
      return { name: 'game-home' };
    } else if (isExperiencePage()) {
      return { name: 'home' };
    } else {
      const orgId = await getOrganisationId();
      if (!orgId) return { name: 'noCompany' };
      return { name: 'home', params: {
        companyId: orgId,
      }};
    }
  }

class StateMachine {
  private state: State = State.START;
  private invitedAccountId: string = null;

  urlIsInvitationLink(to: RouteLocationNormalized, invitationId: string) {
    if (invitationId && to.name === 'invitation') this.state = State.GET_INVITATION_DETAILS;
    else this.state = State.IS_LOGGED_IN;
  }

  async getInvitationDetails(invitationId: string) {
    try {
      const invitationDetails = await invitationApi.getInvitation(invitationId);
      this.invitedAccountId = invitationDetails.account_id;
      this.state = State.IS_LINK_PERSONAL;
    } catch (e) {
      console.error('[authGuard] Failed to fetch invitation', e);
      this.state = State.NOT_AUTHORIZED;
    }
  }

  isLinkPersonal() {
    if (!!this.invitedAccountId) this.state = State.LINK_ID_MATCH_TOKEN;
    else this.state = State.IS_LOGGED_IN;
  }

  async linkIdMatchToken() {
    const token = await fetchTokenQuery();

    const invitedUserId = this.invitedAccountId;
    const tokenUserId = token?.decoded?.sub;

    if (invitedUserId && tokenUserId !== invitedUserId) {
      removeTokenFromCache();
      this.state = State.REGISTER_PAGE_URL_AND_GO_TO_LOGIN;
    }
    else this.state = State.IS_LOGGED_IN;
  }

  alreadyOnLoginPage(to: RouteLocationNormalized) {
    if (to.name === 'auth') this.state = State.STOP;
    else this.state = State.REGISTER_PAGE_URL_AND_GO_TO_LOGIN;
  }

  async isLoggedIn() {
    const token = await fetchTokenQuery();
    if (token?.decoded?.sub) {
      this.invitedAccountId = token?.decoded?.sub as string;
    }
    if (token) this.state = State.ACCEPT_INVITATION;
    else this.state = State.ALREADY_ON_LOGIN_PAGE;
  }

  async acceptInvitation(to: RouteLocationNormalized, invitationId: string) {
    if (invitationId !== undefined && invitationId !== null && to.name === "invitation") {
      await invitationApi.acceptInvitation(invitationId).catch(console.error);
    }
    this.state = State.IS_ON_LOGIN_OR_INVITE_PAGE;
  }

  isOnLoginOrInvitePage(to: RouteLocationNormalized) {
    // If user is already logged in and tries to access auth pages or root ('/') page, go to home page
    if (to.name === "auth" || to.name === 'invitation' || to.name === 'root') this.state = State.REDIRECT_TO_HOME_PAGE;
    else this.state = State.IS_AUTHORIZED;
  }

  async authorized(to: RouteLocationNormalized) {
    if (isTalentPage()) {
      const { candidate, getAccount } = useCandidateStore();
      const cloudClientStore = useCloudClientStore();
      to.meta.layout = AppLayout;
      await getAccount();

      if (!cloudClientStore.canStart() && to.name === "game-waiting") {
       this.state = State.REDIRECT_TO_HOME_PAGE
      } else {
       this.state = State.STOP;
      }
    } else if (isExperiencePage()) {
      to.meta.layout = ExperienceLayout;
      this.state = State.STOP;
    } else {
      prefetchUserRoles().catch(console.error);
      await fetchUserQuery().catch(console.error);
      if (!to.meta.layout) to.meta.layout = Authenticated;
      this.state = State.STOP;
    }
  }


  async loop(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    const invitationId = to.query.invitation_id as string;
    console.log("Invitation ID: ", invitationId);
    console.log("Navigating to: ", to.name);

    let iterationCount = 0;
    while (this.state !== State.STOP && iterationCount < 100) {
      console.log("Current auth state: ", this.state);
      iterationCount++;
      switch (this.state) {
        case State.START:
          this.state = State.URL_IS_INVITATION_LINK;
          break;
        case State.URL_IS_INVITATION_LINK:
          this.urlIsInvitationLink(to, invitationId);
          break;
        case State.GET_INVITATION_DETAILS:
          await this.getInvitationDetails(invitationId);
          break;
        case State.IS_LINK_PERSONAL:
          this.isLinkPersonal();
          break;
        case State.LINK_ID_MATCH_TOKEN:
          await this.linkIdMatchToken();
          break;
        case State.ALREADY_ON_LOGIN_PAGE:
          this.alreadyOnLoginPage(to);
          break;
        case State.REGISTER_PAGE_URL_AND_GO_TO_LOGIN:
          registerCurrentUrl(to, this.invitedAccountId);
          // Go to login page
          this.state = State.NOT_AUTHORIZED;
          break;
        case State.IS_LOGGED_IN:
          await this.isLoggedIn();
          break;
        case State.ACCEPT_INVITATION:
          await this.acceptInvitation(to, invitationId);
          break;
        case State.IS_ON_LOGIN_OR_INVITE_PAGE:
          this.isOnLoginOrInvitePage(to);
          break;
        case State.IS_AUTHORIZED:
          await this.authorized(to);
          break;
        case State.NOT_AUTHORIZED:
          return { name: 'auth', query: { invitation_id: invitationId } };
        case State.REDIRECT_TO_HOME_PAGE:
          return (await redirectToHomePage(to));
      }
    }
    console.log("State machine iteration count: ", iterationCount);
  }

}

export async function authGuard(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  let stateMachine = new StateMachine();
  return (await stateMachine.loop(to, from));
}